import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRentChangeBulkEdit } from "../../hooks/useRentChangeBulkEdit";
import { formatDateShort } from "../../lib/format";
import type { RentChange, Tenant } from "../../lib/realestate";
import { MoneyInput } from "../forms/MoneyInput";
import { ConfirmDialog } from "../ui/ConfirmDialog";

interface Props {
  propertyId: string;
  tenant: Tenant & { rentChanges: RentChange[] };
  onAddRentChange: () => void;
  onEndTenancy: () => void;
  onEdit: () => void;
  onHardDelete?: () => void;
  onDeleteRentChange: (rentChangeId: string) => void;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

function findCurrentRentChange(rentChanges: RentChange[]): RentChange | null {
  const today = todayIso();
  const applicable = rentChanges
    .filter((rc) => rc.effectiveFrom <= today)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
  return applicable[0] ?? null;
}

function findFutureRentChanges(rentChanges: RentChange[]): RentChange[] {
  const today = todayIso();
  return rentChanges
    .filter((rc) => rc.effectiveFrom > today)
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
}

export function TenantCard({
  propertyId,
  tenant,
  onAddRentChange,
  onEndTenancy,
  onEdit,
  onHardDelete,
  onDeleteRentChange,
}: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isActive = tenant.moveOutDate === null;
  const current = findCurrentRentChange(tenant.rentChanges);
  const future = findFutureRentChanges(tenant.rentChanges);

  const bulk = useRentChangeBulkEdit({
    propertyId,
    tenantId: tenant.id,
    rentChanges: tenant.rentChanges,
    moveInDate: tenant.moveInDate,
    moveOutDate: tenant.moveOutDate,
  });

  return (
    <div className="bg-white border rounded p-4 space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">{tenant.name}</h3>
          <p className="text-xs text-slate-500">
            {tenant.unitLabel} · {t("realestate.tenant.move_in")}:{" "}
            {formatDateShort(tenant.moveInDate)}
            {tenant.moveOutDate &&
              ` · ${t("realestate.tenant.move_out")}: ${formatDateShort(tenant.moveOutDate)}`}
          </p>
        </div>
        <span
          className={`px-2 py-0.5 text-xs rounded ${isActive ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}
        >
          {isActive
            ? t("realestate.tenant.status_active")
            : t("realestate.tenant.status_historical")}
        </span>
      </header>

      <div className="text-sm">
        {current ? (
          <>
            <p>
              <strong>{t("realestate.tenant.current_rent")}:</strong>{" "}
              <span className="font-mono">{formatEur(current.coldRentCents)}</span>
              {" + "}
              <span className="font-mono text-slate-500">
                {formatEur(current.sideCostsAdvanceCents)} NK
              </span>
            </p>
            {future.map((rc) => (
              <p key={rc.id} className="text-xs text-amber-700 mt-1">
                {t("realestate.tenant.future_rent_change", {
                  date: formatDateShort(rc.effectiveFrom),
                  rent: formatEur(rc.coldRentCents),
                })}
              </p>
            ))}
          </>
        ) : (
          <p className="text-slate-500">{t("realestate.tenant.no_rent_yet")}</p>
        )}
      </div>

      <details open={expanded} onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}>
        <summary className="cursor-pointer text-sm text-blue-600">
          {t("realestate.tenant.show_history")} ({tenant.rentChanges.length})
        </summary>

        <div className="flex justify-end gap-2 mt-2 mb-2">
          {!bulk.editMode && (
            <button
              type="button"
              className="text-sm px-2 py-1 border rounded"
              onClick={bulk.enterEditMode}
            >
              {t("realestate.tenant.rent_history_edit_mode.enter")}
            </button>
          )}
          {bulk.editMode && (
            <span className="text-xs text-slate-600 mr-auto self-center">
              {bulk.saveState.rowErrors.size > 0 && bulk.saveState.lastSaveAttemptCount > 0
                ? t("realestate.tenant.rent_history_edit_mode.counter_partial", {
                    saved: bulk.saveState.lastSaveAttemptCount - bulk.saveState.rowErrors.size,
                    total: bulk.saveState.lastSaveAttemptCount,
                  })
                : t("realestate.tenant.rent_history_edit_mode.counter", {
                    count: bulk.changesCount,
                  })}
            </span>
          )}
        </div>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b">
              <th className="p-1">{t("realestate.rent_change.effective_from")}</th>
              <th className="p-1 text-right">{t("realestate.rent_change.cold_rent")}</th>
              <th className="p-1 text-right">{t("realestate.rent_change.side_costs_advance")}</th>
              <th className="p-1">{t("realestate.rent_change.notes")}</th>
              <th className="p-1" />
            </tr>
          </thead>
          <tbody>
            {[...tenant.rentChanges]
              .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))
              .map((rc) => {
                const draft = bulk.rows.get(rc.id);
                const editingRow = bulk.editMode && draft !== undefined;
                const rowError = bulk.saveState.rowErrors.get(rc.id);
                return (
                  <tr key={rc.id} className="border-b align-top">
                    <td className="p-1 font-mono text-xs">
                      {editingRow && draft ? (
                        <input
                          type="date"
                          value={draft.effectiveFrom}
                          onChange={(e) => bulk.setRow(rc.id, { effectiveFrom: e.target.value })}
                          className="border rounded px-1 py-0.5 text-xs"
                        />
                      ) : (
                        formatDateShort(rc.effectiveFrom)
                      )}
                    </td>
                    <td className="p-1 text-right font-mono">
                      {editingRow && draft ? (
                        <MoneyInput
                          valueCents={draft.coldRentCents}
                          onChange={(c) => bulk.setRow(rc.id, { coldRentCents: c ?? 0 })}
                          placeholder="0,00"
                          className="border rounded px-1 py-0.5 text-xs w-24 text-right"
                        />
                      ) : (
                        formatEur(rc.coldRentCents)
                      )}
                    </td>
                    <td className="p-1 text-right font-mono">
                      {editingRow && draft ? (
                        <MoneyInput
                          valueCents={draft.sideCostsAdvanceCents}
                          onChange={(c) => bulk.setRow(rc.id, { sideCostsAdvanceCents: c ?? 0 })}
                          placeholder="0,00"
                          className="border rounded px-1 py-0.5 text-xs w-24 text-right"
                        />
                      ) : (
                        formatEur(rc.sideCostsAdvanceCents)
                      )}
                    </td>
                    <td className="p-1 text-xs text-slate-600">
                      {editingRow && draft ? (
                        <input
                          type="text"
                          value={draft.notes ?? ""}
                          onChange={(e) =>
                            bulk.setRow(rc.id, {
                              notes: e.target.value === "" ? null : e.target.value,
                            })
                          }
                          className="border rounded px-1 py-0.5 text-xs w-full"
                        />
                      ) : (
                        (rc.notes ?? "—")
                      )}
                      {rowError && (
                        <div className="text-xs text-red-600 mt-1">
                          {rowError === "BEFORE_MOVE_IN"
                            ? t("realestate.tenant.rent_change_error.before_move_in")
                            : rowError === "AFTER_MOVE_OUT"
                              ? t("realestate.tenant.rent_change_error.after_move_out")
                              : rowError === "INVALID_AMOUNT"
                                ? t("realestate.tenant.rent_change_error.invalid_amount")
                                : rowError}
                        </div>
                      )}
                    </td>
                    <td className="p-1">
                      {!bulk.editMode && (
                        <button
                          type="button"
                          className="text-red-700 text-xs"
                          onClick={() => onDeleteRentChange(rc.id)}
                        >
                          {t("common.delete")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {bulk.editMode && (
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" className="px-3 py-1 border rounded" onClick={bulk.cancelEdit}>
              {t("realestate.tenant.rent_history_edit_mode.cancel")}
            </button>
            <button
              type="button"
              onClick={bulk.save}
              disabled={bulk.changesCount === 0 || bulk.saveState.loading}
              className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {bulk.saveState.loading
                ? t("realestate.tenant.rent_history_edit_mode.save_loading")
                : t("realestate.tenant.rent_history_edit_mode.save")}
            </button>
          </div>
        )}
      </details>

      {bulk.needsDiscardConfirmation && (
        <ConfirmDialog
          open
          title={t("realestate.tenant.rent_history_edit_mode.discard_confirm_title")}
          message={t("realestate.tenant.rent_history_edit_mode.discard_confirm_message", {
            count: bulk.changesCount,
          })}
          confirmLabel={t("realestate.tenant.rent_history_edit_mode.discard_confirm_ok")}
          destructive
          onConfirm={bulk.confirmDiscard}
          onCancel={bulk.abortDiscardConfirmation}
        />
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          className="px-3 py-1 text-sm border rounded"
          onClick={onAddRentChange}
        >
          {t("realestate.tenant.add_rent_change")}
        </button>
        <button
          type="button"
          className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          onClick={onEndTenancy}
          disabled={!isActive}
          title={!isActive ? t("realestate.tenant.already_ended") : undefined}
        >
          {t("realestate.tenant.end_tenancy")}
        </button>
        <button type="button" className="px-3 py-1 text-sm border rounded" onClick={onEdit}>
          {t("common.edit")}
        </button>
        {!isActive && onHardDelete && (
          <button
            type="button"
            className="px-3 py-1 text-sm text-red-700 underline ml-auto"
            onClick={onHardDelete}
          >
            {t("realestate.tenant.hard_delete")}
          </button>
        )}
      </div>
    </div>
  );
}
